import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ItcotLogo } from "@/components/ItcotLogo";
import { OwnedByBadge } from "@/components/OwnedByBadge";
import { WellnessModal } from "@/components/WellnessModal";
import { AdminMenuChip } from "@/components/AdminMenuChip";
import { DeviceStatusBanner } from "@/components/DeviceStatusBanner";
import { useIdleTimer } from "@/hooks/useIdleTimer";
import { useWellnessSettings } from "@/lib/wellnessSettings";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { lockSettings, isSettingsPath } from "@/components/SettingsGate";
import {
  LayoutDashboard,
  CalendarRange,
  BarChart3,
  Settings,
  Menu,
  LogOut,
  RotateCw,
  ClipboardCheck,
  ChevronLeft,
  KeyRound,
  UserSearch,
  Sparkles,
  FileText,
  Wallet,
  Activity,
  HardDrive,
  Smartphone,
  Users,
  MapPin,
  HardHat,
} from "lucide-react";

// Grouped sidebar navigation. Each group has a `label` and a list of `items`.
const NAV_GROUPS = {
  admin: [
    {
      label: "Command",
      items: [
        { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
      ],
    },
    {
      label: "Operations",
      items: [
        { to: "/admin/attendance", label: "Attendance", icon: ClipboardCheck, badgeKey: "pendingPermissions" },
        { to: "/admin/supervisor-monitoring", label: "Supervisor Monitoring", icon: UserSearch },
        { to: "/admin/cleaning-progress", label: "Cleaning Progress", icon: Sparkles },
        { to: "/admin/cleaning-location-requests", label: "Cleaning Location Requests", icon: MapPin, badgeKey: "pendingCleaningLocation" },
        { to: "/admin/workers-at-work", label: "Workers at Work", icon: Users },
        { to: "/admin/cleaning-cycles", label: "Park Pulse Atlas", icon: Activity },
        { to: "/admin/planner", label: "Work Planner", icon: CalendarRange },
      ],
    },
    {
      label: "Quality & Reports",
      items: [
        { to: "/admin/reports", label: "Reports", icon: BarChart3 },
        { to: "/admin/monthly-reports", label: "Monthly Reports", icon: FileText },
        { to: "/admin/salary-report", label: "Salary Report", icon: Wallet },
      ],
    },
    {
      label: "Configuration",
      items: [
        { to: "/admin/settings", label: "Settings", icon: Settings },
        { to: "/admin/devices",  label: "Devices",  icon: Smartphone, badgeKey: "pendingDevices" },
      ],
    },
  ],
  pmc_supervisor: [],
  contractor_supervisor: [],
  supervisor_incharge: [],
  // Contractor Admin — read-only back-office login, one flat group (no
  // sub-sections needed for 5 pages).
  contractor_admin: [
    {
      label: "Operations Dashboard",
      items: [
        { to: "/contractor-admin/dashboard", label: "Contractor Parks Visit Today", icon: MapPin },
        { to: "/contractor-admin/work-captures", label: "Contractor Work Captures by Contractor Supervisor", icon: HardHat },
        { to: "/contractor-admin/workers-at-work", label: "Workers at Work", icon: Users },
        { to: "/contractor-admin/attendance-calendar", label: "Attendance Calendar", icon: CalendarRange },
        { to: "/contractor-admin/feedback", label: "Monthly Feedback", icon: FileText },
      ],
    },
  ],
};

// Routes where the "Back" button should NOT be shown (true landing pages).
const NO_BACK_PATHS = new Set([
  "/admin/dashboard",
  "/pmc/today",
  "/contractor/attendance",
  "/contractor-admin/dashboard",
]);

export function AppShell({ children, title, subtitle, action, onRefresh, hideBack = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const groups = NAV_GROUPS[user?.role] || [];
  // Contractor Admin is a back-office login (like admin) — sidebar + hamburger
  // on mobile, no bottom tab bar / device-approval banner (those are for the
  // single-purpose field-capture apps only).
  const isField = user?.role !== "admin" && user?.role !== "contractor_admin";
  const [pendingPermissions, setPendingPermissions] = useState(0);
  const [pendingDevices, setPendingDevices] = useState(0);
  const [pendingCleaningLocation, setPendingCleaningLocation] = useState(0);
  const pendingDevicesRef = React.useRef(0);
  const [showWellness, setShowWellness] = useState(false);

  // Wellness Nudge: if admin is idle for the configured duration, show the modal.
  // Admin can change the threshold or disable it entirely via the Admin chip menu.
  // Dev/QA override: ?idle=NN in URL forces an NN-second threshold (admin only).
  const wellness = useWellnessSettings();
  const idleOverrideSec = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const v = parseInt(sp.get("idle") || "0", 10);
      return Number.isFinite(v) && v > 0 ? v : 0;
    } catch (e) { return 0; }
  })();
  const idleTimeoutMs = idleOverrideSec > 0
    ? idleOverrideSec * 1000
    : Math.max(1, wellness.thresholdMin) * 60 * 1000;
  const wellnessEnabled = user?.role === "admin" && (idleOverrideSec > 0 || wellness.enabled);
  const { reset: resetIdle } = useIdleTimer({
    timeout: idleTimeoutMs,
    enabled: wellnessEnabled && !showWellness,
    onIdle: () => setShowWellness(true),
  });

  useEffect(() => {
    if (user?.role !== "admin") return;
    let alive = true;
    const refresh = () => {
      api
        .get("/admin/attendance/permissions/pending-count")
        .then((r) => alive && setPendingPermissions(r.data?.count || 0))
        .catch(() => {});
    };
    refresh();
    const id = setInterval(refresh, 30_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [user?.role]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    let alive = true;
    const refresh = () => {
      api
        .get("/admin/cleaning-location-requests/pending-count")
        .then((r) => alive && setPendingCleaningLocation(r.data?.count || 0))
        .catch(() => {});
    };
    refresh();
    const id = setInterval(refresh, 30_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [user?.role]);

  /*
   * Live pending device-approval counter. Polls every 20s while the admin
   * is signed in. On the FIRST refresh after sign-in we just record the
   * baseline count; subsequent refreshes that show a higher count fire
   * a toast so the admin notices the new request without having to be on
   * the Devices page. Clicking the toast navigates straight there.
   */
  useEffect(() => {
    if (user?.role !== "admin") return;
    let alive = true;
    let firstLoad = true;
    const refresh = () => {
      api.get("/admin/devices/pending-count").then((r) => {
        if (!alive) return;
        const n = Number(r.data?.count || 0);
        const prev = pendingDevicesRef.current;
        setPendingDevices(n);
        if (!firstLoad && n > prev) {
          // A new approval request just landed.
          const isOnDevicesPage = window.location.pathname.startsWith("/admin/devices");
          if (!isOnDevicesPage) {
            toast.message("New device approval request", {
              description: `${n - prev} new request${n - prev === 1 ? "" : "s"} waiting. Click to review.`,
              action: { label: "Open Devices", onClick: () => navigate("/admin/devices") },
              duration: 8000,
            });
          }
        }
        firstLoad = false;
        pendingDevicesRef.current = n;
      }).catch(() => {});
    };
    refresh();
    const id = setInterval(refresh, 20_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [user?.role, navigate]);

  useEffect(() => {
    if (!isSettingsPath(location.pathname)) {
      lockSettings();
    }
  }, [location.pathname]);

  const badges = { pendingPermissions, pendingDevices, pendingCleaningLocation };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleRefresh = () => {
    if (onRefresh) onRefresh();
    else window.location.reload();
  };

  const showBack = !hideBack && !NO_BACK_PATHS.has(location.pathname);
  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else if (user?.role === "admin") navigate("/admin/dashboard");
    else if (user?.role === "pmc_supervisor") navigate("/pmc/today");
    else if (user?.role === "contractor_admin") navigate("/contractor-admin/dashboard");
    else navigate("/contractor/attendance");
  };

  const hasNav = groups.length > 0;
  const flatNav = groups.flatMap((g) => g.items);

  return (
    <div className="min-h-screen bg-background">
      {/* sidebar (desktop) — full-height, edge-to-edge, fixed */}
      {hasNav && (
        <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 lg:block" data-testid="sidebar-aside">
          <SidebarNav user={user} groups={groups} badges={badges} activeTo={location.pathname} onLogout={handleLogout} />
        </aside>
      )}

      <div className={`${hasNav ? "lg:pl-64" : ""}`}>
        <div className="px-3 py-3 sm:px-4 sm:py-5 md:px-6 lg:px-8">
          <main className={`min-w-0 ${isField && hasNav ? "pb-28" : "pb-12"}`}>
            {/* page header */}
            <div className="mb-4 sm:mb-5 flex flex-wrap items-start gap-2 sm:gap-3">
              {hasNav && (
                <Sheet>
                  <SheetTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="lg:hidden shrink-0"
                      data-testid="topnav-menu-button"
                      aria-label="Menu"
                    >
                      <Menu className="h-5 w-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-72 bg-slate-900 p-0 text-slate-100 border-slate-800">
                    <SidebarNav user={user} groups={groups} badges={badges} activeTo={location.pathname} mobile onLogout={handleLogout} />
                  </SheetContent>
                </Sheet>
              )}

              {!hasNav && (
                <Link to="/" className="hidden shrink-0 items-center lg:flex" data-testid="page-logo">
                  <ItcotLogo className="h-10" />
                </Link>
              )}

              {showBack && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleBack}
                  aria-label="Back"
                  data-testid="page-back-button"
                  className="shrink-0"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              )}

              <div className="min-w-0 flex-1">
                {title && (
                  <h1 className="font-display text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight truncate" data-testid="page-title">
                    {title}
                  </h1>
                )}
                {subtitle && <p className="mt-0.5 text-[12px] sm:text-sm text-muted-foreground line-clamp-2">{subtitle}</p>}
              </div>

              <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
                {action}
                {user?.role === "admin" ? (
                  <AdminMenuChip />
                ) : (
                  <span
                    className="inline-flex max-w-[140px] truncate rounded-full bg-secondary px-2 py-1 text-[11px] font-medium sm:max-w-none sm:px-2.5"
                    data-testid="role-chip"
                    title={user?.full_name || ""}
                  >
                    {user?.full_name || (user?.role === "pmc_supervisor" ? "Park Supervisor" : user?.role === "supervisor_incharge" ? "Supervisor In-Charge" : "Contractor")}
                  </span>
                )}
                <Button variant="outline" size="icon" onClick={handleRefresh} aria-label="Refresh" data-testid="page-refresh-button" className="h-9 w-9">
                  <RotateCw className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={handleLogout} aria-label="Sign out" data-testid="page-signout-button" className="h-9 w-9 sm:hidden">
                  <LogOut className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleLogout} data-testid="page-signout-button-lg" className="hidden sm:inline-flex">
                  <LogOut className="mr-1.5 h-4 w-4" />
                  Sign out
                </Button>
              </div>
            </div>

            {isField ? (
              <div className="mb-3">
                <DeviceStatusBanner />
              </div>
            ) : null}

            {children}
          </main>
        </div>
      </div>

      {isField && hasNav && (
        <nav className="fixed inset-x-0 bottom-0 z-30 border-t bg-background/95 backdrop-blur lg:hidden">
          <div className="mx-auto flex max-w-md items-center justify-around px-3 py-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)]">
            {flatNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex min-w-[64px] flex-col items-center gap-0.5 rounded-md px-2 py-1.5 text-[11px] ${
                    isActive ? "text-primary" : "text-muted-foreground"
                  }`
                }
                data-testid={`bottomnav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      )}

      <OwnedByBadge />
      <WellnessModal
        open={showWellness}
        onClose={() => { setShowWellness(false); resetIdle(); }}
      />
    </div>
  );
}

/**
 * Dark-navy sidebar — modelled after the reference dashboard.
 *   bg: slate-900 with light slate text
 *   sections: muted uppercase headers
 *   active item: amber-tinted background + amber text + amber left border
 */
function SidebarNav({ user, groups, badges = {}, activeTo, mobile = false, onLogout }) {
  return (
    <div className={`flex h-full flex-col bg-slate-900 text-slate-100 ${mobile ? "" : "border-r border-slate-800"}`} data-testid="sidebar-nav">
      {/* Header — brand + user line */}
      <div className="flex items-center gap-3 border-b border-slate-800 px-4 py-3.5">
        <div className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-md bg-white">
          <ItcotLogo className="h-7" />
        </div>
        <div className="min-w-0">
          <div className="text-[13px] font-semibold leading-tight">ITCOT Limited</div>
          {user && (
            <div className="text-[11px] text-slate-400 truncate">
              {(user.full_name || user.username || "Admin") === (user.role === "admin" ? "Admin" : "")
                ? "Admin"
                : <>
                    {user.full_name || user.username || "Admin"}
                    <span className="mx-1">·</span>
                    {user.role === "admin" ? "Admin" : user.role === "pmc_supervisor" ? "PMC" : user.role === "supervisor_incharge" ? "In-Charge" : "Contractor"}
                  </>}
            </div>
          )}
        </div>
      </div>

      {/* Groups */}
      <nav className="flex flex-1 flex-col gap-3 overflow-y-auto px-2 py-3" aria-label="Primary">
        {groups.map((g) => (
          <div key={g.label} className="flex flex-col gap-0.5">
            <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {g.label}
            </div>
            {g.items.map((item) => {
              const active = activeTo === item.to || (item.to !== "/" && activeTo?.startsWith(item.to + "/")) || activeTo?.startsWith(item.to);
              const badgeCount = item.badgeKey ? Number(badges[item.badgeKey] || 0) : 0;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={() =>
                    `relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${
                      active
                        ? "bg-amber-500/15 text-amber-300 font-medium"
                        : "text-slate-300 hover:bg-slate-800/70 hover:text-slate-100"
                    }`
                  }
                  data-testid={`sidenav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {active && (
                    <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-amber-400" aria-hidden />
                  )}
                  <item.icon className={`h-4 w-4 ${active ? "text-amber-300" : "text-slate-400"}`} />
                  <span className="flex-1">{item.label}</span>
                  {badgeCount > 0 && (
                    <span
                      className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${
                        active ? "bg-amber-400 text-slate-900" : "bg-rose-500 text-white"
                      }`}
                      data-testid={`sidenav-badge-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {badgeCount}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer — change password + sign out */}
      <div className="flex flex-col gap-0.5 border-t border-slate-800 px-2 py-2.5">
        <Link
          to="/admin/settings"
          className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-slate-300 hover:bg-slate-800/70 hover:text-slate-100"
          data-testid="sidenav-change-password"
        >
          <KeyRound className="h-4 w-4 text-slate-400" />
          <span>Change Password</span>
        </Link>
        <button
          type="button"
          onClick={onLogout}
          className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-slate-300 hover:bg-slate-800/70 hover:text-slate-100"
          data-testid="sidenav-signout"
        >
          <LogOut className="h-4 w-4 text-slate-400" />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );
}

export function RequireRole({ user, roles, children }) {
  if (!user) return null;
  if (!roles.includes(user.role)) return null;
  return children;
}
